import { FC, PropsWithChildren, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { createSupportFormSchema, SupportFormValues } from '@/modules/common';
import { useCreateSupportRequest } from '@/modules/common/hooks/use-create-support-request';

interface SupportModalProps extends PropsWithChildren {
  hideForm?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SupportModal: FC<SupportModalProps> = ({
  children,
  hideForm = false,
  open: controlledOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation('common');
  const [internalOpen, setInternalOpen] = useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const schema = createSupportFormSchema(t);
  const form = useForm<SupportFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { issue: '' },
    mode: 'onChange',
  });

  const onClose = (open: boolean) => {
    form.reset();
    setOpen(open);
  };

  const createSupportRequestMutation = useCreateSupportRequest({
    onSuccess: () => {
      onClose(false);
    },
  });

  const handleSubmit = (data: SupportFormValues) => {
    createSupportRequestMutation.mutate(data.issue);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}

      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="mb-2">{t('support.title')}</DialogTitle>
          <DialogDescription>{t('support.description')}</DialogDescription>
        </DialogHeader>

        {hideForm ? (
          <div className="mt-4 rounded-md border bg-muted/50 p-3">
            <p className="text-sm font-medium text-foreground">{t('support.linkOnly.label')}</p>
            <a
              href="https://tally.so/r/VLJEYv"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-medium text-primary underline-offset-4 hover:underline"
            >
              {t('support.linkOnly.linkText')}
            </a>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="pt-4">
              <FormField
                control={form.control}
                name="issue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">{t('form.issue.input')}*</FormLabel>

                    <FormControl>
                      <Textarea
                        placeholder={t('form.issue.placeholder')}
                        rows={5}
                        className="max-h-[218px] min-h-[38px] resize-y"
                        {...field}
                      />
                    </FormControl>

                    {field.value && <FormMessage />}
                  </FormItem>
                )}
              />

              <div className="mt-6 rounded-md border bg-muted/50 p-3">
                <p className="text-sm font-medium text-foreground">
                  {t('support.detailedForm.label')}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t('support.detailedForm.description')}
                </p>
                <a
                  href="https://tally.so/r/VLJEYv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-xs font-medium text-primary underline-offset-4 hover:underline"
                >
                  {t('support.detailedForm.linkText')}
                </a>
              </div>

              <DialogFooter className="mt-6">
                <DialogClose asChild>
                  <ButtonLoadable
                    type="button"
                    variant="outline"
                    disabled={createSupportRequestMutation.isPending}
                  >
                    {t('cancel')}
                  </ButtonLoadable>
                </DialogClose>

                <ButtonLoadable
                  type="submit"
                  disabled={!form.formState.isValid}
                  isLoading={createSupportRequestMutation.isPending}
                >
                  {t('sendMessage')}
                </ButtonLoadable>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
